import type { MyOmeMissingField } from '../../../../util/genomicIngestionApi';
import type { MyOmeRequisition } from './useMyOmeRequisition';

/**
 * What the clinic sees for a requisition without an RQ id, from its GIS
 * reason code (FLENG-176 "Copy"). `label` is the short tile label, approved
 * by Josh. `explanation` and `nextStep` are the details modal's, still drafts.
 * There's no `explanation` when it would only repeat the label. GIS's own
 * reason text is never shown.
 */
export type MyOmeRequisitionCopy = {
  label: string;
  explanation?: string;
  nextStep?: string;
};

export type MyOmeRequisitionCopyInput = Pick<
  MyOmeRequisition,
  'businessStatus' | 'reasonCode' | 'failureClass' | 'missing' | 'collection'
>;

const CONTACT_SUPPORT = 'Contact support.';

// MyOme reports other statuses too, and can add new ones.
const MYOME_ORDER_STATUS_LABELS: Record<string, string> = {
  AWAITING_SAMPLE: 'Waiting for sample',
  ANALYZING: 'Analyzing',
  CLINICIAN_REVIEW: 'Clinician review',
  COMPLETED: 'Completed',
};

const HELD_OR_FAILED_STATUSES = new Set([
  'AWAITING_PARITY',
  'AWAITING_GATES',
  'PLACING',
  'FAILED',
]);

/** MyOme has the order, and it isn't held or failed. */
export const isPlacedMyOmeRequisition = ({
  rqId,
  businessStatus,
}: Pick<MyOmeRequisition, 'rqId' | 'businessStatus'>) =>
  Boolean(rqId) && !HELD_OR_FAILED_STATUSES.has(businessStatus ?? '');

/**
 * MyOme's status for a placed order. A delivered report is complete even on
 * Tasks from before GIS polled MyOme.
 */
export const getPlacedMyOmeStatus = ({
  myomeStatus,
  businessStatus,
}: Pick<MyOmeRequisition, 'myomeStatus' | 'businessStatus'>) =>
  myomeStatus ??
  (businessStatus === 'REPORT_CREATED' ? 'COMPLETED' : undefined);

/**
 * The tile label for a placed order. Anything without an agreed label, or not
 * polled yet, reads "Placed": it's true for every placed order, and none of
 * them needs support.
 */
const getMyOmeOrderStatusLabel = (status: string | undefined) =>
  (status && MYOME_ORDER_STATUS_LABELS[status]) || 'Placed';

export const getPlacedMyOmeRequisitionCopy = (
  requisition: Pick<MyOmeRequisition, 'myomeStatus' | 'businessStatus'>,
): MyOmeRequisitionCopy => ({
  label: getMyOmeOrderStatusLabel(getPlacedMyOmeStatus(requisition)),
  explanation: 'Placed with MyOme.',
});

const CONSENT_EXPLANATION =
  "Waiting on the patient's Informed Consent for Genome Analysis.";

const OUTCOME_UNKNOWN: MyOmeRequisitionCopy = {
  label: 'Order failed',
  explanation: "We couldn't confirm whether MyOme received the order.",
  nextStep: "Contact support. Don't place a new order.",
};

const LEGACY_ADDRESS_FAILED: MyOmeRequisitionCopy = {
  label: 'Order failed',
  explanation:
    "MyOme couldn't be ordered because the patient's address or state is missing.",
  nextStep: 'Complete the address in Elation, then Re-run.',
};

const WAITING_ON_ORIGINAL: MyOmeRequisitionCopy = {
  label: 'Waiting on original order',
  nextStep: 'It goes out automatically.',
};

const ON_HOLD: MyOmeRequisitionCopy = {
  label: 'On hold',
  explanation: 'The MyOme order is on hold.',
  nextStep: CONTACT_SUPPORT,
};

const FAILED: MyOmeRequisitionCopy = {
  label: 'Order failed',
  explanation: 'The MyOme order failed.',
  nextStep: CONTACT_SUPPORT,
};

const ADDRESS_MISSING_EXPLANATIONS: Record<
  MyOmeRequisition['collection'],
  string
> = {
  'blood-on-site':
    "MyOme needs the patient's complete home address, and it's missing or incomplete.",
  'buccal-home-kit':
    "The swab kit can't ship because the patient's home address is missing or incomplete.",
};

// GIS lists the Pre-Screening survey and its answers as `survey`, so any of
// them means the patient has Pre-Screening to finish.
const iprsPackageCopy = (
  missing: MyOmeMissingField[],
): MyOmeRequisitionCopy => {
  const sources = new Set(missing.map(({ source }) => source));
  if (sources.has('survey')) {
    return {
      label: 'Waiting on Pre-Screening',
      explanation: 'The iPRS order is waiting on the Pre-Screening survey.',
      nextStep:
        'Ask the patient to complete the Pre-Screening survey, then Re-run.',
    };
  }
  if (sources.has('lab-or-vital')) {
    return {
      label: 'Missing lab values',
      explanation: 'The iPRS order is missing lab values.',
      nextStep: 'Record the missing lab values, then Re-run.',
    };
  }
  if (sources.has('consent')) {
    return {
      label: 'Missing consent',
      explanation: CONSENT_EXPLANATION,
      nextStep: 'Ask the patient to complete the consent, then Re-run.',
    };
  }
  // Rows parked before GIS stored the list.
  return ON_HOLD;
};

type CopyRule = (
  input: MyOmeRequisitionCopyInput & { failed: boolean },
) => MyOmeRequisitionCopy;

const COPY: Record<string, CopyRule> = {
  placing: () => ({
    label: 'Being placed',
    explanation: 'Being placed with MyOme.',
  }),
  'consent-missing': () => ({
    label: 'Missing consent',
    explanation: CONSENT_EXPLANATION,
    nextStep:
      'Ask the patient to complete the Informed Consent for Genome Analysis, then Re-run.',
  }),
  'patient-address-missing': ({ failed, collection }) =>
    failed
      ? LEGACY_ADDRESS_FAILED
      : {
          label: 'Missing address',
          explanation: ADDRESS_MISSING_EXPLANATIONS[collection],
          nextStep:
            "Complete the patient's home address in Elation, then Re-run. Don't place a new order.",
        },
  // Legacy: GIS sends a missing state as `patient-address-missing` now.
  'patient-jurisdiction-unknown': () => LEGACY_ADDRESS_FAILED,
  'ordering-provider-not-found': () => ({
    label: 'Missing provider',
    explanation:
      "The ordering physician couldn't be matched to a provider record.",
    nextStep: "Check the physician's NPI in Elation, then Re-run.",
  }),
  'ordering-clinic-missing': () => ({
    label: 'Missing clinic',
    explanation: "This clinic isn't set up to order MyOme yet.",
    nextStep:
      'Contact support. The order goes through once the clinic is set up.',
  }),
  'lab-order-incomplete': () => ({
    label: 'Incomplete lab order',
    explanation:
      'The Elation lab order is missing required details, such as the ordering physician.',
    nextStep: 'Update the MyOme lab order in Elation.',
  }),
  'new-york-patient': () => ({
    label: 'Not available in NY',
    explanation: "MyOme isn't available for patients in New York.",
  }),
  'new-york-center': () => ({
    label: 'Not available in NY',
    explanation: "MyOme isn't available for New York centers.",
  }),
  'placement-error': () => ({
    label: "Couldn't send",
    explanation: "The order couldn't be sent to MyOme.",
    nextStep: 'Re-run. If it fails again, contact support.',
  }),
  'myome-config-error': () => ({
    label: 'Order failed',
    explanation: "MyOme couldn't accept the order because of a setup problem.",
    nextStep: CONTACT_SUPPORT,
  }),
  'myome-rejected': ({ failureClass }) => {
    if (failureClass === 'vendor-unknown') {
      return OUTCOME_UNKNOWN;
    }
    return {
      label: 'Order failed',
      explanation: 'MyOme rejected the order.',
      // Legacy rows have no class and aren't re-runnable.
      nextStep:
        failureClass === 'vendor-rejected'
          ? 'Fix what MyOme named, then Re-run.'
          : CONTACT_SUPPORT,
    };
  },
  'myome-outcome-unknown': () => OUTCOME_UNKNOWN,
  'report-delivery-failed': () => ({
    label: 'Report failed',
    explanation: "MyOme's report couldn't be delivered to the chart.",
    nextStep: CONTACT_SUPPORT,
  }),
  'original-requisition-pending': () => WAITING_ON_ORIGINAL,
  'original-requisition-incomplete': () => WAITING_ON_ORIGINAL,
  'original-requisition-failed': () => ({
    label: WAITING_ON_ORIGINAL.label,
    explanation:
      "The original requisition failed, so the iPRS order can't go out.",
    nextStep: CONTACT_SUPPORT,
  }),
  'iprs-package-incomplete': ({ missing }) => iprsPackageCopy(missing),
  'iprs-data-invalid': () => ({
    label: 'Survey data problem',
    explanation: "The survey or lab data couldn't be used.",
    nextStep: CONTACT_SUPPORT,
  }),
  'iprs-products-already-ordered': () => ({
    label: 'Already ordered',
    explanation:
      'The requested iPRS products were already ordered for this patient.',
  }),
};

export const isMyOmeRequisitionFailed = (businessStatus: string | undefined) =>
  businessStatus === 'FAILED';

export const getMyOmeRequisitionCopy = (
  input: MyOmeRequisitionCopyInput,
): MyOmeRequisitionCopy => {
  const failed = isMyOmeRequisitionFailed(input.businessStatus);
  const rule = input.reasonCode ? COPY[input.reasonCode] : undefined;
  // Includes GIS's `unknown`, a missing code, and codes newer than this build.
  if (!rule) {
    return failed ? FAILED : ON_HOLD;
  }
  return rule({ ...input, failed });
};
